import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Autocomplete,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  AccountBalance,
  Refresh,
  Add,
  Check,
  Close,
  Help,
  CheckCircle,
} from '@mui/icons-material';
import { financeService, Account } from '../../services/financeService';
import { BankReconciliation, BankReconciliationItem } from '../../types/finance';
import {
  ReportHeader,
  IndianAmountDisplay,
} from '../../components/finance';

const BankReconciliationPage: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [reconciliations, setReconciliations] = useState<BankReconciliation[]>([]);
  const [activeReconciliation, setActiveReconciliation] = useState<BankReconciliation | null>(null);
  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // New Reconciliation Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newRecDate, setNewRecDate] = useState(new Date().toISOString().split('T')[0]);
  const [newStatementBalance, setNewStatementBalance] = useState('');
  const [creating, setCreating] = useState(false);

  // Fetch bank accounts on mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const result = await financeService.getAccounts({ accountType: 'ASSET' });
        // Filter to only bank accounts (you may need to adjust this logic)
        const bankAccounts = result.accounts.filter(
          (a) => a.account_code.startsWith('1100') || a.account_name.toLowerCase().includes('bank')
        );
        setAccounts(bankAccounts);
      } catch (err: any) {
        console.error('Error fetching accounts:', err);
        setError('Failed to load bank accounts');
      } finally {
        setAccountsLoading(false);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch reconciliations when account changes
  const fetchReconciliations = async () => {
    if (!selectedAccount) return;

    setLoading(true);
    setError(null);
    try {
      const data = await financeService.getBankReconciliations(selectedAccount.id);
      setReconciliations(data);
      // Auto-select the first in-progress reconciliation
      const inProgress = data.find((r) => r.status === 'IN_PROGRESS');
      if (inProgress) {
        setActiveReconciliation(inProgress);
      } else {
        setActiveReconciliation(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load reconciliations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAccount) {
      fetchReconciliations();
    }
  }, [selectedAccount]);

  const handleCreateReconciliation = async () => {
    if (!selectedAccount || !newStatementBalance) return;

    setCreating(true);
    setError(null);
    try {
      const newRec = await financeService.createBankReconciliation({
        bankAccountId: selectedAccount.id,
        reconciliationDate: newRecDate,
        statementBalance: parseFloat(newStatementBalance),
      });
      setReconciliations([newRec, ...reconciliations]);
      setActiveReconciliation(newRec);
      setDialogOpen(false);
      setNewStatementBalance('');
      setSuccess('Bank reconciliation created successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to create reconciliation');
    } finally {
      setCreating(false);
    }
  };

  const handleReconcileItem = async (itemId: number, status: 'MATCHED' | 'UNMATCHED' | 'ADJUSTED') => {
    if (!activeReconciliation) return;

    try {
      const updated = await financeService.reconcileItem(
        activeReconciliation.id,
        itemId,
        status
      );
      setActiveReconciliation(updated);
      setReconciliations(
        reconciliations.map((r) => (r.id === updated.id ? updated : r))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update item');
    }
  };

  const handleCompleteReconciliation = async () => {
    if (!activeReconciliation) return;

    try {
      const completed = await financeService.completeBankReconciliation(activeReconciliation.id);
      setActiveReconciliation(completed);
      setReconciliations(
        reconciliations.map((r) => (r.id === completed.id ? completed : r))
      );
      setSuccess('Bank reconciliation completed successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to complete reconciliation');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'MATCHED':
        return 'success';
      case 'UNMATCHED':
        return 'error';
      case 'ADJUSTED':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Clear messages after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
            Bank Reconciliation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Match bank statement entries with book entries
          </Typography>
        </Box>
        {selectedAccount && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            New Reconciliation
          </Button>
        )}
      </Box>

      {/* Account Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={accounts}
              getOptionLabel={(option) => `${option.account_code} - ${option.account_name}`}
              value={selectedAccount}
              onChange={(_, newValue) => {
                setSelectedAccount(newValue);
                setActiveReconciliation(null);
              }}
              loading={accountsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Bank Account"
                  size="small"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <AccountBalance fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button
              variant="outlined"
              onClick={fetchReconciliations}
              disabled={!selectedAccount || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : activeReconciliation ? (
        <>
          {/* Reconciliation Header */}
          <ReportHeader
            companyName="Your Company Name"
            reportTitle="Bank Reconciliation"
            subtitle={`${activeReconciliation.bank_account_name}`}
            asOnDate={activeReconciliation.reconciliation_date}
          />

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Statement Balance</Typography>
                  <IndianAmountDisplay amount={activeReconciliation.statement_balance} bold size="large" />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Book Balance</Typography>
                  <IndianAmountDisplay amount={activeReconciliation.book_balance} bold size="large" />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Reconciled Balance</Typography>
                  <IndianAmountDisplay amount={activeReconciliation.reconciled_balance} bold size="large" />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Card sx={{ bgcolor: activeReconciliation.difference === 0 ? 'success.50' : 'error.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Difference</Typography>
                  <IndianAmountDisplay
                    amount={activeReconciliation.difference}
                    bold
                    size="large"
                    color={activeReconciliation.difference === 0 ? 'positive' : 'negative'}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Progress Summary */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 8 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip
                    label={`Total: ${activeReconciliation.summary.totalItems}`}
                    size="small"
                  />
                  <Chip
                    label={`Matched: ${activeReconciliation.summary.matchedItems}`}
                    size="small"
                    color="success"
                  />
                  <Chip
                    label={`Unmatched: ${activeReconciliation.summary.unmatchedItems}`}
                    size="small"
                    color="error"
                  />
                  <Chip
                    label={`Pending: ${activeReconciliation.summary.pendingItems}`}
                    size="small"
                    color="warning"
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: 'right' }}>
                {activeReconciliation.status === 'IN_PROGRESS' && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={handleCompleteReconciliation}
                    disabled={activeReconciliation.difference !== 0}
                  >
                    Complete Reconciliation
                  </Button>
                )}
                {activeReconciliation.status === 'COMPLETED' && (
                  <Chip
                    label="COMPLETED"
                    color="success"
                    icon={<CheckCircle />}
                  />
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Items Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Entry No.</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reference</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Debit (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Credit (₹)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeReconciliation.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No items to reconcile</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  activeReconciliation.items.map((item: BankReconciliationItem) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        bgcolor: item.status === 'MATCHED' ? 'success.50' :
                                 item.status === 'UNMATCHED' ? 'error.50' : undefined,
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace' }}>{item.entry_number}</TableCell>
                      <TableCell>{new Date(item.entry_date).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.reference || '-'}</TableCell>
                      <TableCell align="right">
                        {item.debit_amount > 0 && <IndianAmountDisplay amount={item.debit_amount} />}
                      </TableCell>
                      <TableCell align="right">
                        {item.credit_amount > 0 && <IndianAmountDisplay amount={item.credit_amount} />}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          size="small"
                          color={getStatusColor(item.status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        {activeReconciliation.status === 'IN_PROGRESS' && item.status === 'PENDING' && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Mark as Matched">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleReconcileItem(item.id, 'MATCHED')}
                              >
                                <Check fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark as Unmatched">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleReconcileItem(item.id, 'UNMATCHED')}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark as Adjusted">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleReconcileItem(item.id, 'ADJUSTED')}
                              >
                                <Help fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : selectedAccount ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <AccountBalance sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Active Reconciliation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click "New Reconciliation" to start reconciling bank statements
          </Typography>
          {reconciliations.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" gutterBottom>
                Previous Reconciliations
              </Typography>
              <Box sx={{ mt: 2 }}>
                {reconciliations.slice(0, 5).map((rec) => (
                  <Chip
                    key={rec.id}
                    label={`${new Date(rec.reconciliation_date).toLocaleDateString('en-IN')} - ${rec.status}`}
                    sx={{ m: 0.5 }}
                    color={rec.status === 'COMPLETED' ? 'success' : 'default'}
                    onClick={() => setActiveReconciliation(rec)}
                  />
                ))}
              </Box>
            </>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <AccountBalance sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a bank account to start reconciliation
          </Typography>
        </Paper>
      )}

      {/* New Reconciliation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Bank Reconciliation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Bank Account"
              fullWidth
              disabled
              value={selectedAccount ? `${selectedAccount.account_code} - ${selectedAccount.account_name}` : ''}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Reconciliation Date"
              type="date"
              fullWidth
              value={newRecDate}
              onChange={(e) => setNewRecDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Statement Balance"
              type="number"
              fullWidth
              value={newStatementBalance}
              onChange={(e) => setNewStatementBalance(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                },
              }}
              helperText="Enter the closing balance as per bank statement"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateReconciliation}
            disabled={!newStatementBalance || creating}
          >
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankReconciliationPage;
